import React from 'react';
import { showNotification } from '@common/services';
import { Icon } from '@common/components';
import { ERROR_TYPES } from './error-handler-constants';
import {
  getCodeFromErrors,
  notifyUnhandledError,
} from './error-handler-service';
import {
  StandardNotFoundErrorMessage,
  ContextualNotFoundErrorMessage,
  NotificationNotFoundErrorMessage,
} from './error-handler-error-not-found';
import {
  StandardUnexpectedErrorMessage,
  ContextualUnexpectedErrorMessage,
  NotificationUnexpectedErrorMessage,
} from './error-handler-error-unexpected';
import {
  StandardNoAccessErrorMessage,
  ContextualNoAccessErrorMessage,
  NotificationNoAccessErrorMessage,
} from './error-handler-error-no-access';
import { ApolloError } from '@common/hooks';
import { errorHandlerShowErrorNotification } from './_error-handler-show-error-notification';

function showErrorNotification(message: JSX.Element) {
  showNotification({
    message,
    icon: <Icon icon="timesCircle" kind="regular" color="coral" size="xl" />,
    duration: 0,
  });
}

function getErrorMessagesByType(errorCode: string | undefined) {
  return (
    {
      [ERROR_TYPES.NOT_FOUND]: {
        standard: <StandardNotFoundErrorMessage />,
        contextual: <ContextualNotFoundErrorMessage />,
        notification: <NotificationNotFoundErrorMessage />,
      },
      [ERROR_TYPES.UNAUTHORIZED]: {
        standard: <StandardNoAccessErrorMessage />,
        contextual: <ContextualNoAccessErrorMessage />,
        notification: <NotificationNoAccessErrorMessage />,
      },
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    }[errorCode!] || {
      standard: <StandardUnexpectedErrorMessage />,
      contextual: <ContextualUnexpectedErrorMessage />,
      notification: <NotificationUnexpectedErrorMessage />,
    }
  );
}

function getErrorMessage(
  errorCode: string | undefined,
  messageType: 'contextual' | 'notification' | undefined
) {
  const isContextual = messageType === 'contextual';
  const { standard, contextual, notification } =
    getErrorMessagesByType(errorCode);

  if (isContextual) {
    showErrorNotification(notification);
  }

  if (messageType === 'notification') {
    return notification;
  }

  return isContextual ? contextual : standard;
}

export function ErrorHandler({
  error,
  messageType,
  log,
}: {
  error: ApolloError | undefined;
  messageType?: 'contextual';
  log: string;
}) {
  if (!error || !error.graphQLErrors) {
    notifyUnhandledError(error || new Error(`Unknown error from ${log}`), log);

    return getErrorMessage(ERROR_TYPES.UNEXPECTED, messageType);
  }

  const errorCode = getCodeFromErrors(error.graphQLErrors);

  if (!errorCode || errorCode === ERROR_TYPES.UNEXPECTED) {
    notifyUnhandledError(error, log);
  }

  return getErrorMessage(errorCode, messageType);
}

ErrorHandler.showErrorNotification = errorHandlerShowErrorNotification;
